import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CallTask,
  Pagination,
  lifecycleAPI,
  lifecycleErrorMessage,
} from '@/services/lifecycleAPI';

import { CallTasksParams } from '../useLifecycleUrlState';

export const CALL_TASKS_PER_PAGE = 25;

const useCallTasks = (
  params: Pick<CallTasksParams, 'status' | 'journey' | 'mine' | 'page'>,
  myUserId: number | null,
) => {
  const { status, journey, mine, page } = params;
  const [tasks, setTasks] = useState<CallTask[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const silent = useRef(false);

  useEffect(() => {
    let active = true;
    if (!silent.current) setLoading(true);
    silent.current = false;
    setError(null);
    lifecycleAPI
      .listCallTasks({
        status,
        ...(journey ? { journey_key: journey } : {}),
        ...(mine && myUserId !== null ? { assigned_to: myUserId } : {}),
        page,
        per_page: CALL_TASKS_PER_PAGE,
      })
      .then((result) => {
        if (!active) return;
        setTasks(result.data);
        setPagination(result.pagination);
      })
      .catch((err) => {
        if (active) {
          setError(
            lifecycleErrorMessage(err, 'Could not load the call tasks.'),
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [status, journey, mine, myUserId, page, attempt]);

  const reload = useCallback(() => {
    silent.current = true;
    setAttempt((n) => n + 1);
  }, []);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { tasks, pagination, loading, error, reload, retry };
};

export default useCallTasks;
